import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface NewsPreferences {
  user_id: string;
  fetch_frequency: 'manual' | 'daily' | 'twice_daily' | 'weekly';
  max_articles_per_fetch: number;
  min_relevance_score: number;
  enabled_categories: string[];
  tracked_industry_keywords: string[];
  track_contact_companies: boolean;
  industry_focus: string;
  additional_companies: string[];
  notify_high_relevance: boolean;
  notify_company_mentions: boolean;
  last_fetch_at?: string;
  created_at?: string;
  updated_at?: string;
}

const defaultPreferences: Omit<NewsPreferences, 'user_id'> = {
  fetch_frequency: 'daily',
  max_articles_per_fetch: 10,
  min_relevance_score: 0.60,
  enabled_categories: [
    'industry_trends',
    'technology',
    'regulations',
    'safety',
    'sustainability',
    'company_news'
  ],
  tracked_industry_keywords: ['construction', 'building', 'contractors'],
  track_contact_companies: true,
  industry_focus: 'General Construction',
  additional_companies: [],
  notify_high_relevance: false,
  notify_company_mentions: false,
};

export const useNewsPreferences = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['news-preferences'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('news_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      // If no preferences exist, create default ones
      if (!data) {
        const { data: newPrefs, error: insertError } = await supabase
          .from('news_preferences')
          .insert({ ...defaultPreferences, user_id: user.id })
          .select()
          .single();

        if (insertError) throw insertError;
        return newPrefs as NewsPreferences;
      }

      return data as NewsPreferences;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<NewsPreferences>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('news_preferences')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-preferences'] });
      toast({
        title: "Preferences saved",
        description: "Your news feed preferences have been updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error saving preferences",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    preferences,
    isLoading,
    error,
    updatePreferences: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
};
